import { faX } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo } from 'react';

import type { OptionIdentifier } from '@/types/optionDiscovery';
import { volatilityService } from '@services/volatilityService';
import type { Asset } from '@shared/types';
import { brandColors } from '@utils/color-utils';
import { calculateLambda } from '@utils/leverage';
import { calculateOptionGreeks } from '@utils/option-math';

interface GreeksModalProps {
  isVisible: boolean;
  option: OptionIdentifier | null;
  spotPrice: number | null;
  optionPrice: number | null;
  volatility?: number;
  onClose: () => void;
}

interface GreekDisplay {
  label: string;
  value: number | null;
  description: string;
  format: (val: number) => string;
}

export function GreeksModal({
  isVisible,
  option,
  spotPrice,
  optionPrice,
  volatility: volatilityOverride,
  onClose,
}: GreeksModalProps) {
  const greeks = useMemo(() => {
    if (!option || !spotPrice || spotPrice <= 0) {
      return null;
    }

    const volatility =
      volatilityOverride ?? volatilityService.getVolatility(option.asset);
    if (!volatility || volatility <= 0) {
      return null;
    }

    const timeToExpiryMs = option.expiryTimestamp - Date.now();
    if (timeToExpiryMs <= 0) {
      return null;
    }

    // Convert time to expiry from milliseconds to years
    const timeToExpiryYears = timeToExpiryMs / (365 * 24 * 60 * 60 * 1000);

    const params = {
      underlyingPrice: spotPrice,
      strikePrice: option.strikePrice,
      timeToExpiry: timeToExpiryYears,
      riskFreeRate: 0.04, // 4% risk-free rate
      volatility,
      optionType: (option.type === 'CALL' ? 'call' : 'put') as 'call' | 'put',
    };

    const calculatedGreeks = calculateOptionGreeks(params);

    // Calculate Lambda (leverage) using the same utility function as the list
    const lambda = calculateLambda({
      option,
      spotPrice,
      optionPrice: optionPrice ?? 0,
      volatility,
    });

    return {
      ...calculatedGreeks,
      lambda,
      iv: volatility, // Implied Volatility
    };
  }, [option, spotPrice, optionPrice, volatilityOverride]);

  const greekDisplays: GreekDisplay[] = useMemo(() => {
    if (!greeks) {
      return [];
    }

    return [
      {
        label: 'Delta',
        value: greeks.delta,
        description: 'Price sensitivity to underlying price change',
        format: (val: number) => val.toFixed(4),
      },
      {
        label: 'Gamma',
        value: greeks.gamma,
        description: 'Delta sensitivity to underlying price change',
        format: (val: number) => val.toFixed(6),
      },
      {
        label: 'Theta',
        value: greeks.theta,
        description: 'Price sensitivity to time decay (per day)',
        format: (val: number) => `$${val.toFixed(4)}`,
      },
      {
        label: 'Vega',
        value: greeks.vega,
        description: 'Price sensitivity to volatility change (per 1% vol)',
        format: (val: number) => `$${val.toFixed(4)}`,
      },
      {
        label: 'Rho',
        value: greeks.rho,
        description: 'Price sensitivity to interest rate change (per 1% rate)',
        format: (val: number) => `$${val.toFixed(4)}`,
      },
      {
        label: 'Lambda',
        value: greeks.lambda,
        description: 'Leverage (Delta × Spot Price / Option Price)',
        format: (val: number) => `${val.toFixed(2)}x`,
      },
      {
        label: 'IV',
        value: greeks.iv,
        description: 'Implied Volatility',
        format: (val: number) => `${(val * 100).toFixed(2)}%`,
      },
    ];
  }, [greeks]);

  if (!option) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-40"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed inset-0 z-50 flex flex-col bg-brand-dark"
            style={{ backgroundColor: 'var(--brand-dark)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
              <div>
                <h2 className="text-white text-xl font-bold">Option Greeks</h2>
                <p className="text-white/60 text-sm mt-1">
                  {option.asset} {option.type === 'CALL' ? 'ABOVE' : 'BELOW'}{' '}
                  ${option.strikePrice.toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </p>
              </div>
              <motion.button
                className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FontAwesomeIcon icon={faX} />
              </motion.button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-6">
              {!greeks ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-white/60 text-center">
                    <p className="text-lg mb-2">Unable to calculate Greeks</p>
                    <p className="text-sm">
                      Missing required data (spot price, volatility, or option
                      price)
                    </p>
                  </div>
                </div>
              ) : (
                <div className="max-w-2xl mx-auto space-y-4">
                  {greekDisplays.map((greek, index) => (
                    <motion.div
                      key={greek.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white/5 rounded-lg p-4 border border-white/10"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg mb-1">
                            {greek.label}
                          </h3>
                          <p className="text-white/60 text-sm">
                            {greek.description}
                          </p>
                        </div>
                        <div className="ml-4 text-right">
                          {greek.value !== null ? (
                            <div className="text-white font-bold text-xl">
                              {greek.format(greek.value)}
                            </div>
                          ) : (
                            <div className="text-white/40 text-xl">-</div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {/* Additional Info */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: greekDisplays.length * 0.05 }}
                    className="bg-white/5 rounded-lg p-4 border border-white/10 mt-6"
                  >
                    <h3 className="text-white font-semibold text-lg mb-3">
                      Additional Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/60">Spot Price</span>
                        <span className="text-white">
                          {spotPrice
                            ? `$${spotPrice.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Option Price</span>
                        <span className="text-white">
                          {optionPrice
                            ? `$${optionPrice.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })}`
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

